import { useMemo, useState } from 'react'
import { feature } from 'topojson-client'
import { geoPath } from 'd3-geo'
import world from '@/assets/world.topojson'
import { fitMercatorToBbox, projectLatLon } from '@/lib/geo'
import { REGIONS, getRegionForIso2, getCountryLabel } from '@/assets/region-mapping'
import type { RegionSlug, ThreatMapData } from '@/types/map'

export interface RegionMapProps {
  data: ThreatMapData
  region: RegionSlug
  onDrillCountry: (iso2: string) => void
  isLoading: boolean
  error: unknown
}

/**
 * Natural-Earth 110m TopoJSON only carries `iso_a2` on ~69/177 features.
 * The remaining 108 features (covering the majority of Central Asia, the
 * Horn of Africa, the Gulf, the Caribbean, and several politically sensitive
 * territories) only carry the ISO3166 numeric `id` and `name`. Fall back to
 * a numeric→alpha2 map so SP6 region/country views can resolve every
 * country that appears in the ISO2_TO_REGION table.
 */
const NUMERIC_TO_ISO2: Record<string, string> = {
  '004': 'AF', '010': 'AQ', '024': 'AO', '031': 'AZ', '044': 'BS',
  '051': 'AM', '064': 'BT', '068': 'BO', '070': 'BA', '072': 'BW',
  '084': 'BZ', '090': 'SB', '096': 'BN', '104': 'MM', '108': 'BI',
  '116': 'KH', '120': 'CM', '140': 'CF', '144': 'LK', '148': 'TD',
  '178': 'CG', '180': 'CD', '188': 'CR', '192': 'CU', '196': 'CY',
  '204': 'BJ', '214': 'DO', '218': 'EC', '222': 'SV', '226': 'GQ',
  '232': 'ER', '238': 'FK', '242': 'FJ', '260': 'TF', '262': 'DJ',
  '266': 'GA', '268': 'GE', '270': 'GM', '275': 'PS', '288': 'GH',
  '304': 'GL', '320': 'GT', '324': 'GN', '328': 'GY', '332': 'HT',
  '340': 'HN', '352': 'IS', '372': 'IE', '384': 'CI', '388': 'JM',
  '398': 'KZ', '410': 'KR', '414': 'KW', '417': 'KG', '418': 'LA',
  '422': 'LB', '426': 'LS', '430': 'LR', '434': 'LY', '442': 'LU',
  '450': 'MG', '454': 'MW', '466': 'ML', '478': 'MR', '496': 'MN',
  '498': 'MD', '499': 'ME', '508': 'MZ', '512': 'OM', '516': 'NA',
  '524': 'NP', '540': 'NC', '548': 'VU', '558': 'NI', '562': 'NE',
  '591': 'PA', '598': 'PG', '600': 'PY', '624': 'GW', '626': 'TL',
  '630': 'PR', '634': 'QA', '646': 'RW', '686': 'SN', '694': 'SL',
  '706': 'SO', '716': 'ZW', '728': 'SS', '729': 'SD', '732': 'EH',
  '740': 'SR', '748': 'SZ', '762': 'TJ', '768': 'TG', '780': 'TT',
  '788': 'TN', '795': 'TM', '800': 'UG', '807': 'MK', '834': 'TZ',
  '854': 'BF', '858': 'UY', '860': 'UZ', '887': 'YE', '894': 'ZM',
}

function resolveIso2(featureObj: {
  id?: string | number
  properties?: Record<string, unknown>
}): string {
  const props = featureObj.properties ?? {}
  const iso = props.iso_a2 ?? props.ISO_A2 ?? props.ISO3166_1_ALPHA2
  if (typeof iso === 'string' && iso.length === 2) return iso
  const id = featureObj.id != null ? String(featureObj.id).padStart(3, '0') : ''
  return NUMERIC_TO_ISO2[id] ?? ''
}

export function RegionMap({
  data,
  region,
  onDrillCountry,
  isLoading,
  error,
}: RegionMapProps) {
  const W = 800
  const H = 600
  const meta = REGIONS[region]
  const proj = useMemo(
    () => fitMercatorToBbox(meta.bbox, { width: W, height: H, padding: 24 }),
    [meta],
  )
  const path = useMemo(() => geoPath(proj), [proj])
  const countries = useMemo(
    () =>
      (
        feature(
          world as unknown as Parameters<typeof feature>[0],
          (world as unknown as { objects: Record<string, unknown> }).objects.countries as Parameters<
            typeof feature
          >[1],
        ) as unknown as { features: Array<{ id?: string; properties?: Record<string, unknown> }> }
      ).features,
    [],
  )
  const [hover, setHover] = useState<{ iso2: string; x: number; y: number } | null>(null)

  const incidentsByIso2 = useMemo(() => {
    const out = new Map<string, number>()
    for (const inc of data.incidents) {
      const t = data.targets.find((x) => x.id === inc.targetId)
      if (!t) continue
      out.set(t.countryIso2, (out.get(t.countryIso2) ?? 0) + 1)
    }
    return out
  }, [data])

  if (isLoading) {
    return (
      <div
        data-testid="region-skeleton"
        className="h-full w-full animate-pulse bg-[color:var(--surface-secondary)]"
      />
    )
  }
  if (error) {
    return (
      <div className="p-4 text-[color:var(--severity-high)]">Failed to load region data.</div>
    )
  }

  return (
    <div className="relative h-full w-full">
      <div className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 font-mono text-xs uppercase tracking-widest text-[color:var(--text-secondary)]">
        {meta.label}
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${meta.label} region view`}
        className="h-full w-full"
      >
        {countries.map((c, i) => {
          const iso2 = resolveIso2(c)
          const inRegion = iso2 ? getRegionForIso2(iso2) === region : false
          return (
            <path
              key={i}
              data-testid={iso2 ? `country-${iso2}` : undefined}
              data-in-region={inRegion ? 'true' : 'false'}
              d={path(c as unknown as Parameters<typeof path>[0]) ?? ''}
              fill={inRegion ? 'var(--surface-secondary)' : 'var(--surface-tertiary)'}
              fillOpacity={inRegion ? 1 : 0.4}
              stroke={inRegion ? 'var(--border-default)' : 'var(--border-subtle)'}
              strokeWidth={inRegion ? 0.8 : 0.4}
              onMouseEnter={(e) => setHover({ iso2, x: e.clientX, y: e.clientY })}
              onMouseLeave={() => setHover(null)}
              onClick={() => {
                if (inRegion && iso2) onDrillCountry(iso2)
              }}
              style={{ cursor: inRegion ? 'pointer' : 'default' }}
            />
          )
        })}
        {data.incidents.map((inc) => {
          const t = data.targets.find((x) => x.id === inc.targetId)
          if (!t || getRegionForIso2(t.countryIso2) !== region) return null
          const p = projectLatLon(proj, t.latitude, t.longitude)
          if (!p) return null
          return (
            <g key={inc.id} data-testid="region-marker">
              <circle
                cx={p[0]}
                cy={p[1]}
                r={8}
                fill={`var(--severity-${inc.severity})`}
                fillOpacity={0.3}
              />
              <circle cx={p[0]} cy={p[1]} r={3} fill={`var(--severity-${inc.severity})`} />
            </g>
          )
        })}
      </svg>
      {hover && hover.iso2 && (
        <div
          role="tooltip"
          className="pointer-events-none absolute rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-elevated)] px-2 py-1 text-xs"
          style={{ left: hover.x + 8, top: hover.y + 8 }}
        >
          {getCountryLabel(hover.iso2, hover.iso2)} — {incidentsByIso2.get(hover.iso2) ?? 0} incidents
        </div>
      )}
    </div>
  )
}
